import { Color, NormalBlending, ShaderMaterial } from 'three';
import WebGLObject from '../_app/cuchillo/3D/WebGLObject';
import { PLANE_GEOMETRY } from './constants';
import gsap from 'gsap';

// Cercle de llum (com un godray però MOLT més simple): un cercle/oval d'un color amb un degradat
// radial cap a les vores → s'integra amb el fons com un núvol. El color ve del `data-color` del
// `.__target` del bloc. Mateix flux que Godrays: WebGLObject mapejat al DOM, segueix l'scroll, va al
// 2n render (underwater). Blending normal (no additiu) perquè el color es fongui amb el fons.
export const LIGHTCIRCLE_SETTINGS = {
  opacity: 0.1,     // opacitat al centre
  radius: 1.02,      // fins on arriba el cercle (1 ≈ vores del target)
  softness: 1.07,
  rotation: 2.45   // amplada del degradat (↑ = més difuminat, es fon abans amb el fons)
};

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;

const FRAG = /* glsl */`
  precision highp float;
  varying vec2 vUv;
  uniform vec3 uColor;
  uniform float uOpacity, uRadius, uSoftness;
  void main() {
    float d = length((vUv - 0.5) * 2.0);                 // 0 centre → ~1 a les vores (oval segons aspect)
    float a = smoothstep(uRadius, uRadius - uSoftness, d) * uOpacity;
    if (a <= 0.001) discard;
    gl_FragColor = vec4(uColor, a);                       // NormalBlending → degradat que es fon amb el fons
  }
`;

export default class LightCircle extends WebGLObject {
  constructor(dom) {
    super({ dom, geometry: PLANE_GEOMETRY, hasMouse: false });
    const raw = this.dom.getAttribute('data-color');
    this._color = new Color(raw && raw.trim() ? raw.trim() : '#bfe0ff'); // fallback clar
    this._setupMaterial();
    this.init();
    this.resize();
  }

  _setupMaterial() {
    const s = LIGHTCIRCLE_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uColor: { value: this._color },
        uOpacity: { value: 0 }, // arrenca ocult; show() quan entra l'scroll-item
        uRadius: { value: s.radius },
        uSoftness: { value: s.softness },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: NormalBlending,
    });
  }

  setLayer(n) { this.traverse((o) => o.layers.set(n)); }

  show() {
    gsap.killTweensOf(this.material.uniforms.uOpacity);
    gsap.to(this.material.uniforms.uOpacity, { value: LIGHTCIRCLE_SETTINGS.opacity, duration: .7 });
  }

  hide() {
    gsap.killTweensOf(this.material.uniforms.uOpacity);
    gsap.to(this.material.uniforms.uOpacity, { value: 0, duration: .4 });
  }

  loop() {
    this._place();
  }

  setConfig(partial = {}) {
    Object.assign(LIGHTCIRCLE_SETTINGS, partial);
    if (!this.material) return;
    const u = this.material.uniforms;
    const s = LIGHTCIRCLE_SETTINGS;
    // No forçar opacitat aquí: la controlen show/hide (si no, el pane/reconfig les fa visibles)
    u.uRadius.value = s.radius;
    u.uSoftness.value = s.softness;
  }

  _place() {
    if (!this.dom) return;
    const { x, y, width, height } = this.dom.getBoundingClientRect();
    const p = this.domPositionTo3D(x, y);
    this.position.x = p.x + width * 0.5;
    this.position.y = p.y - height * 0.5;
    this.rotation.z = LIGHTCIRCLE_SETTINGS.rotation;
  }

  resize() {
    if (!this.dom || !this.mesh) return;
    const { width, height } = this.dom.getBoundingClientRect();
    super.resize(width, height); // mesh.scale = mida DOM en px
    this._place();
  }

  dispose() { super.dispose(); }
}
